import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, Globe, Send, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '@/context/LanguageContext';
import { SEO } from '@/components/seo/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: '',
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Prefill message when arriving from a pricing package CTA (?pakket=...)
  useEffect(() => {
    const pkg = new URLSearchParams(window.location.search).get('pakket');
    if (!pkg) return;
    const intros = {
      nl: `Ik ben geïnteresseerd in het pakket "${pkg}". `,
      en: `I'm interested in the "${pkg}" package. `,
      fr: `Je suis intéressé(e) par le forfait "${pkg}". `,
      de: `Ich interessiere mich für das Paket "${pkg}". `,
    };
    const autoIntros = Object.values(intros);
    setFormData((prev) => {
      if (prev.message === '' || autoIntros.includes(prev.message)) {
        return { ...prev, message: intros[language] || intros.nl };
      }
      return prev;
    });
  }, [language]);

  // SEO translations
  const seoText = {
    nl: {
      title: 'Contact - Neem Contact Op voor een Gratis Offerte',
      description: 'Neem contact op met Netblitz voor een vrijblijvende offerte. We reageren binnen 24 uur.'
    },
    en: {
      title: 'Contact - Get in Touch for a Free Quote',
      description: 'Contact Netblitz for a free quote. We respond within 24 hours.'
    },
    fr: {
      title: 'Contact - Contactez-nous pour un Devis Gratuit',
      description: 'Contactez Netblitz pour un devis gratuit. Nous répondons sous 24 heures.'
    },
    de: {
      title: 'Kontakt - Kontaktieren Sie uns für ein Kostenloses Angebot',
      description: 'Kontaktieren Sie Netblitz für ein kostenloses Angebot. Wir antworten innerhalb von 24 Stunden.'
    }
  };

  const currentSeo = seoText[language] || seoText.nl;

  const services = [
    { value: 'website', label: t('contactPage.serviceOptions.website') },
    { value: 'redesign', label: t('contactPage.serviceOptions.redesign') },
    { value: 'seo', label: t('contactPage.serviceOptions.seo') },
    { value: 'support', label: t('contactPage.serviceOptions.support') },
    { value: 'other', label: t('contactPage.serviceOptions.other') },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, formData);
      if (response.data.success) {
        setStatus({ 
          type: 'success', 
          message: t('contactPage.formSuccess')
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          service: '',
          message: '',
        });
      }
    } catch (error) {
      setStatus({ 
        type: 'error', 
        message: t('contactPage.formError')
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main data-testid="contact-page">
      <SEO 
        title={currentSeo.title}
        description={currentSeo.description}
        keywords="contact webdesign, offerte website, website laten maken contact"
      />
      {/* Hero */}
      <section className="pt-24 sm:pt-32 pb-12 sm:pb-20 bg-gradient-to-b from-[#F3F4F6] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-[1.75rem] sm:text-4xl md:text-5xl lg:text-6xl font-bold text-[#111827] mb-4 sm:mb-6 leading-tight">
              {t('contactPage.title')} <span className="text-gradient">{t('contactPage.titleHighlight')}</span> {t('contactPage.titleEnd')}
            </h1>
            <p className="text-base sm:text-xl text-[#4B5563]">
              {t('contactPage.description')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div>
                <h2 className="text-2xl font-bold text-[#111827] mb-6">
                  {t('contactPage.directContact')}
                </h2>
                <p className="text-[#4B5563] mb-8">
                  {t('contactPage.directDescription')}
                </p>
              </div>

              <div className="space-y-6">
                <a 
                  href="mailto:hello@netblitz.eu"
                  className="flex items-start gap-4 p-4 bg-white rounded-2xl border border-gray-200 hover:border-[#2563EB]/30 hover:shadow-lg transition-all group"
                  data-testid="contact-email"
                >
                  <div className="w-12 h-12 bg-[#2563EB]/10 rounded-xl flex items-center justify-center group-hover:bg-[#2563EB] transition-colors">
                    <Mail className="w-6 h-6 text-[#2563EB] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#111827]">{t('contactPage.email')}</p>
                    <p className="text-[#4B5563]">hello@netblitz.eu</p>
                  </div>
                </a>

                <div className="flex items-start gap-4 p-4 bg-white rounded-2xl border border-gray-200 hover:border-[#2563EB]/30 hover:shadow-lg transition-all group">
                  <div className="w-12 h-12 bg-[#2563EB]/10 rounded-xl flex items-center justify-center group-hover:bg-[#2563EB] transition-colors">
                    <Phone className="w-6 h-6 text-[#2563EB] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#111827]">{t('contactPage.phone')}</p>
                    <a href="tel:+31851155703" className="text-[#4B5563] hover:text-[#2563EB] block" data-testid="contact-phone-nl">
                      NL: 085 115 5703
                    </a>
                    <a href="tel:+32484448854" className="text-[#4B5563] hover:text-[#2563EB] block" data-testid="contact-phone-be">
                      BE: 0484 448 854
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-white rounded-2xl border border-gray-200">
                  <div className="w-12 h-12 bg-[#2563EB]/10 rounded-xl flex items-center justify-center">
                    <Globe className="w-6 h-6 text-[#2563EB]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#111827]">{t('contactPage.region')}</p>
                    <p className="text-[#4B5563]">{t('contactPage.worldwide')}</p>
                  </div>
                </div>
              </div>

              {/* Response time */}
              <div className="bg-[#2563EB]/5 p-6 rounded-2xl">
                <p className="text-[#2563EB] font-semibold mb-2">{t('contactPage.responseTime')}</p>
                <p className="text-[#4B5563] text-sm">
                  {t('contactPage.responseDescription')}
                </p>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-2"
            >
              <form 
                onSubmit={handleSubmit} 
                className="bg-white p-8 md:p-10 rounded-3xl border border-gray-200 shadow-lg"
                data-testid="contact-form"
              >
                <h2 className="text-2xl font-bold text-[#111827] mb-8">
                  {t('contactPage.formTitle')}
                </h2>

                {status.message && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`p-4 rounded-xl mb-6 flex items-center gap-3 ${
                      status.type === 'success' 
                        ? 'bg-green-50 text-green-800' 
                        : 'bg-red-50 text-red-800'
                    }`}
                    data-testid="form-status"
                  >
                    {status.type === 'success' ? (
                      <CheckCircle className="w-5 h-5 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    )}
                    <span>{status.message}</span>
                  </motion.div>
                )}

                <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-[#111827] mb-2">
                      {t('contactPage.formName')} *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3.5 rounded-xl border border-gray-200 bg-[#F9FAFB] focus:bg-white focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/10 transition-all text-base"
                      placeholder={t('contactPage.formName')}
                      data-testid="input-name"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-[#111827] mb-2">
                      {t('contactPage.formEmail')} *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3.5 rounded-xl border border-gray-200 bg-[#F9FAFB] focus:bg-white focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/10 transition-all text-base"
                      placeholder="email@example.com"
                      data-testid="input-email"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-[#111827] mb-2">
                      {t('contactPage.formPhone')}
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3.5 rounded-xl border border-gray-200 bg-[#F9FAFB] focus:bg-white focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/10 transition-all text-base"
                      placeholder="+32 xxx xxx xxx"
                      data-testid="input-phone"
                    />
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-[#111827] mb-2">
                      {t('contactPage.formCompany')}
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3.5 rounded-xl border border-gray-200 bg-[#F9FAFB] focus:bg-white focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/10 transition-all text-base"
                      placeholder={t('contactPage.formCompany')}
                      data-testid="input-company"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label htmlFor="service" className="block text-sm font-medium text-[#111827] mb-2">
                      {t('contactPage.formService')} *
                    </label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3.5 rounded-xl border border-gray-200 bg-[#F9FAFB] focus:bg-white focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/10 transition-all text-base appearance-none"
                      data-testid="input-service"
                    >
                      <option value="">{t('contactPage.formServicePlaceholder')}</option>
                      {services.map((service) => (
                        <option key={service.value} value={service.value}>
                          {service.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label htmlFor="message" className="block text-sm font-medium text-[#111827] mb-2">
                      {t('contactPage.formMessage')} *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="w-full px-4 py-3.5 rounded-xl border border-gray-200 bg-[#F9FAFB] focus:bg-white focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/10 transition-all resize-none text-base"
                      placeholder={t('contactPage.formMessagePlaceholder')}
                      data-testid="input-message"
                    />
                  </div>
                </div>

                {/* 50% Deposit Notice */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3 mt-6">
                  <span className="text-amber-500 text-xl flex-shrink-0">💡</span>
                  <div>
                    <p className="text-sm text-amber-800 font-medium">50% voorschot bij aanvang</p>
                    <p className="text-xs text-amber-700 mt-1">
                      Bij akkoord wordt 50% voorschot gevraagd voor de start van het project. Het resterende bedrag wordt gefactureerd bij oplevering.
                    </p>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full mt-6 sm:mt-8 flex items-center justify-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-semibold py-4 px-6 rounded-full shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed min-h-[52px] text-base"
                  data-testid="submit-button"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {t('contactPage.formSubmitting')}
                    </>
                  ) : (
                    <>
                      {t('contactPage.formSubmit')}
                      <Send size={20} />
                    </>
                  )}
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ContactPage;
